import React, { ComponentPropsWithoutRef, useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

interface ParticlesProps extends ComponentPropsWithoutRef<"div"> {
  className?: string;
  quantity?: number;
  staticity?: number;
  ease?: number;
  size?: number;
  refresh?: boolean;
  color?: string;
  vx?: number;
  vy?: number;
  fps?: number;
  maxDpr?: number;
  motionEnabled?: boolean;
}

type Circle = {
  x: number;
  y: number;
  translateX: number;
  translateY: number;
  size: number;
  alpha: number;
  targetAlpha: number;
  dx: number;
  dy: number;
  magnetism: number;
};

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((character) => character + character)
          .join("")
      : normalized;
  const hexInt = parseInt(value, 16);

  return [(hexInt >> 16) & 255, (hexInt >> 8) & 255, hexInt & 255];
}

export const Particles: React.FC<ParticlesProps> = ({
  className = "",
  quantity = 70,
  staticity = 50,
  ease = 50,
  size = 0.4,
  refresh = false,
  color = "#ffffff",
  vx = 0,
  vy = 0,
  fps = 60,
  maxDpr = 1.5,
  motionEnabled = true,
  ...props
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !container || !context) return;

    let circles: Circle[] = [];
    let width = 0;
    let height = 0;
    let dpr = 1;
    let rafId: number | null = null;
    let lastFrame = 0;
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const mouse = { x: 0, y: 0 };
    const [red, green, blue] = hexToRgb(color);
    const frameDuration = 1000 / Math.max(1, fps);
    const hasFinePointer = window.matchMedia("(pointer: fine)").matches;

    const circleParams = (): Circle => {
      const targetAlpha = Number((Math.random() * 0.6 + 0.1).toFixed(1));

      return {
        x: Math.floor(Math.random() * width),
        y: Math.floor(Math.random() * height),
        translateX: 0,
        translateY: 0,
        size: Math.floor(Math.random() * 2) + size,
        alpha: motionEnabled ? 0 : targetAlpha,
        targetAlpha,
        dx: (Math.random() - 0.5) * 0.1,
        dy: (Math.random() - 0.5) * 0.1,
        magnetism: 0.1 + Math.random() * 4,
      };
    };

    const seedParticles = () => {
      circles = Array.from({ length: quantity }, circleParams);
    };

    const clearCanvas = () => {
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.restore();
    };

    const drawCircle = (circle: Circle) => {
      context.beginPath();
      context.arc(
        circle.x + circle.translateX,
        circle.y + circle.translateY,
        circle.size,
        0,
        Math.PI * 2
      );
      context.globalAlpha = circle.alpha;
      context.fill();
    };

    const drawFrame = (update: boolean) => {
      clearCanvas();
      // One color for every particle; each one's alpha goes through
      // globalAlpha rather than a new rgba() string to parse per draw.
      context.fillStyle = `rgb(${red}, ${green}, ${blue})`;

      for (let index = circles.length - 1; index >= 0; index -= 1) {
        const circle = circles[index]!;

        if (update) {
          const closestEdge = Math.min(
            circle.x + circle.translateX - circle.size,
            width - circle.x - circle.translateX - circle.size,
            circle.y + circle.translateY - circle.size,
            height - circle.y - circle.translateY - circle.size
          );
          const edgeAlpha = Math.min(1, Math.max(0, closestEdge / 20));

          circle.alpha =
            edgeAlpha >= 1
              ? Math.min(circle.targetAlpha, circle.alpha + 0.02)
              : circle.targetAlpha * edgeAlpha;
          circle.x += circle.dx + vx;
          circle.y += circle.dy + vy;
          circle.translateX +=
            (mouse.x / (staticity / circle.magnetism) - circle.translateX) /
            ease;
          circle.translateY +=
            (mouse.y / (staticity / circle.magnetism) - circle.translateY) /
            ease;

          if (
            circle.x < -circle.size ||
            circle.x > width + circle.size ||
            circle.y < -circle.size ||
            circle.y > height + circle.size
          ) {
            circles[index] = circleParams();
          }
        }

        drawCircle(circles[index]!);
      }
    };

    const resizeCanvas = () => {
      const nextWidth = container.offsetWidth;
      const nextHeight = container.offsetHeight;
      const nextDpr = Math.min(window.devicePixelRatio || 1, maxDpr);

      if (width === nextWidth && height === nextHeight && dpr === nextDpr) {
        return;
      }

      width = nextWidth;
      height = nextHeight;
      dpr = nextDpr;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedParticles();
      drawFrame(false);
    };

    const scheduleFrame = () => {
      if (rafId === null && motionEnabled && !document.hidden) {
        rafId = window.requestAnimationFrame(animateFrame);
      }
    };

    const animateFrame = (time: number) => {
      rafId = null;

      if (time - lastFrame >= frameDuration - 1) {
        lastFrame = time;
        drawFrame(true);
      }

      scheduleFrame();
    };

    const handleResize = () => {
      if (resizeTimeout !== null) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(resizeCanvas, 200);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left - width / 2;
      const y = event.clientY - rect.top - height / 2;

      if (Math.abs(x) < width / 2 && Math.abs(y) < height / 2) {
        mouse.x = x;
        mouse.y = y;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      } else {
        scheduleFrame();
      }
    };

    resizeCanvas();
    scheduleFrame();
    window.addEventListener("resize", handleResize);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (hasFinePointer) window.addEventListener("mousemove", handleMouseMove);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (resizeTimeout !== null) window.clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (hasFinePointer)
        window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [
    color,
    ease,
    fps,
    maxDpr,
    motionEnabled,
    quantity,
    refresh,
    size,
    staticity,
    vx,
    vy,
  ]);

  return (
    <div
      className={cn("pointer-events-none", className)}
      ref={canvasContainerRef}
      aria-hidden="true"
      {...props}
    >
      <canvas ref={canvasRef} className="size-full" />
    </div>
  );
};
